import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { newUUID } from '@ricoh/ria.client.core/utils/data/key';

import type {
  ProcessFlowLayout,
  ProcessFlowNode,
  ProcessFlowNodeAdd,
  ProcessFlowNodeEnd,
  ProcessFlowNodeStart,
  ProcessFlowNodeStep,
  ProcessFlowStep,
} from '~/common/model/api/batch-type/process-flow';

import type { ChildNodeResult, CreateNodeOptions, ProcessFlowNodeWithInBoundEdges } from './_node.type';

import { getInboundEdges, getOutboundEdges } from './_edge';

const NODE_BASE: Partial<ProcessFlowNode> = {
  deletable: false,
};

/** CreateNode. */
export const createNode = {
  start: ({ position = { x: 0, y: 0 }, offset = { x: 0, y: 0 }, ...options }: CreateNodeOptions): ProcessFlowNodeStart => ({
    ...NODE_BASE,
    ...options,
    id: newUUID(),
    type: 'start',
    position: {
      x: position.x + offset.x,
      y: position.y + offset.y,
    },
    data: {},
  }),

  end: ({ position = { x: 0, y: 0 }, offset = { x: 0, y: 0 }, ...options }: CreateNodeOptions): ProcessFlowNodeEnd => ({
    ...NODE_BASE,
    ...options,
    id: newUUID(),
    type: 'end',
    position: {
      x: position.x + offset.x,
      y: position.y + offset.y,
    },
    data: {},
  }),

  add: ({ position = { x: 0, y: 0 }, offset = { x: 0, y: 0 }, ...options }: CreateNodeOptions<Pick<ProcessFlowNodeAdd, 'data'>>): ProcessFlowNodeAdd =>
    ({
      ...NODE_BASE,
      ...options,
      id: newUUID(),
      type: 'add',
      position: {
        x: position.x + offset.x,
        y: position.y + offset.y,
      },
    }) as ProcessFlowNodeAdd,

  step: ({
    id,
    position = { x: 0, y: 0 },
    offset = { x: 0, y: 0 },
    ...options
  }: CreateNodeOptions<Pick<ProcessFlowNodeStep, 'data'>>): ProcessFlowNodeStep => ({
    ...NODE_BASE,
    ...options,
    id: id ?? newUUID(),
    type: 'step',
    position: {
      x: position.x + offset.x,
      y: position.y + offset.y,
    },
  }),
};

/**
 * Get node.
 *
 * @param {ProcessFlowLayout} layout Layout.
 * @param {string} id Node id.
 *
 * @returns {ProcessFlowNode | undefined} Node.
 */
export function getNode(layout: ProcessFlowLayout, id: string): ProcessFlowNode | undefined {
  return layout.nodes.find((node) => node.id === id);
}

/**
 * Get Start Node.
 *
 * @param {ProcessFlowLayout} layout Layout.
 *
 * @returns {ProcessFlowNodeStart | undefined} Start Node.
 */
export function getStartNode(layout: ProcessFlowLayout): ProcessFlowNodeStart | undefined {
  return layout.nodes.find((node) => node.type === 'start');
}

/**
 * Get parent nodes.
 *
 * @param {ProcessFlowLayout} layout Layout.
 * @param {string} id Node id.
 *
 * @returns {ProcessFlowNode[]} Parent nodes.
 */
export function getParentNodes(layout: ProcessFlowLayout, id: string): ProcessFlowNode[] {
  return Object.values(
    getInboundEdges(layout, id).reduce<Record<string, ProcessFlowNode>>((prev, edge) => {
      const sourceNode = getNode(layout, edge.source);
      if (sourceNode && !prev[sourceNode.id]) {
        prev[sourceNode.id] = sourceNode;
      }

      return prev;
    }, {}),
  );
}

/**
 * Get child nodes.
 *
 * @param {ProcessFlowLayout} layout Layout.
 * @param {string} id Node id.
 *
 * @returns {ChildNode[]} Child nodes.
 */
export function getChildNodes(layout: ProcessFlowLayout, id: string): ChildNodeResult {
  const nodes: ProcessFlowNode[] = [];
  const nodesWithInboundEdges = getOutboundEdges(layout, id).reduce<Record<string, ProcessFlowNodeWithInBoundEdges>>((prev, edge) => {
    const targetNode = getNode(layout, edge.target);
    if (targetNode) {
      const childNode = prev[targetNode.id];
      if (childNode) {
        childNode.inboundEdges.push(edge);
      } else {
        nodes.push(targetNode);
        prev[targetNode.id] = {
          node: targetNode,
          inboundEdges: [edge],
        };
      }
    }

    return prev;
  }, {});

  return {
    nodes,
    nodesWithInboundEdges: Object.values(nodesWithInboundEdges),
  };
}

/**
 * Get flow nodes.
 *
 * @param {ProcessFlowLayout} layout Layout.
 * @param {string} id Node id.
 * @param {Func<[childNodes: ChildNodeResult], boolean>} predicate Predicate.
 *
 * @returns {ProcessFlowNode[]} Flow nodes.
 */
export function getFlowNodes(layout: ProcessFlowLayout, id: string, predicate: Func<[childNodes: ChildNodeResult], boolean> = () => true): ProcessFlowNode[] {
  const results: Record<string, ProcessFlowNode> = {};

  (function build(parent: string) {
    const childNodes = getChildNodes(layout, parent);
    if (!predicate(childNodes)) {
      return;
    }

    childNodes.nodes.forEach((childNode) => {
      if (Object.keys(results).includes(childNode.id)) {
        return;
      }

      results[childNode.id] ??= childNode;
      build(childNode.id);
    });
  })(id);

  return Object.values(results);
}

/**
 * Get Condition child nodes.
 *
 * @param {ProcessFlowLayout} layout Layout.
 * @param {string} id Node id.
 *
 * @returns {ProcessFlowNode[]} Flow nodes.
 */
export function getConditionChildNodes(layout: ProcessFlowLayout, id: string): ProcessFlowNode[] {
  return getFlowNodes(layout, id, (nodes) => {
    const isConvergentNode = nodes.nodesWithInboundEdges.some((nodeWithInboundEdges) =>
      nodeWithInboundEdges.inboundEdges.some((edge) => edge.type === 'default' && edge.data.breakable && edge.data.rootId === id),
    );

    return !isConvergentNode;
  });
}

/**
 * Get ProcessFlow Node Step.
 *
 * @param {ProcessFlowLayout} layout Layout.
 * @param {ProcessFlowStep} step Step.
 *
 * @returns {ProcessFlowNodeStep | undefined} ProcessFlow Node Step.
 */
export function getNodeStep(layout: ProcessFlowLayout, step: ProcessFlowStep): ProcessFlowNodeStep | undefined {
  return layout.nodes.find((node): node is ProcessFlowNodeStep => node.type === 'step' && node.id === step.attributes.key);
}

/**
 * Get Convergent Node.
 *
 * @param {ProcessFlowLayout} layout ProcessFlowLayout.
 * @param {string} conditionId Condition Node Id.
 *
 * @returns {ProcessFlowNodeStep | undefined} ProcessFlow Node Step.
 */
export function getConvergentNode(layout: ProcessFlowLayout, conditionId?: string): ProcessFlowNode | undefined {
  if (!conditionId) {
    return undefined;
  }
  return layout.nodes.find((node) => node.type === 'add' && node.data.rootId === conditionId);
}
